import React, { useState, useEffect } from 'react'
import DriverAvatar from './DriverAvatar'
import ManualAssignment from './ManualAssignment'

interface Driver { number: number; name: string; team: string }
interface Props {
  onSetupComplete: (gameId: string, assignments: any[], prizes: any) => void
  gameSlug?: string   // if set, posts to /api/games/:slug/setup instead
  token?: string      // auth token for game-scoped setup
}

export default function PlayerSetup({ onSetupComplete, gameSlug, token }: Props) {
  const [playerCount, setPlayerCount] = useState(4)
  const [playerNames,  setPlayerNames]  = useState<string[]>(Array(4).fill(''))
  const [playerEmails, setPlayerEmails] = useState<string[]>(Array(4).fill(''))
  const [prizes, setPrizes] = useState({ first: 50, second: 30, third: 20 })
  const [drivers, setDrivers] = useState<Driver[]>([])
  const [driverSource, setDriverSource] = useState('')
  const [loading, setLoading] = useState(false)
  const [fetchingDrivers, setFetchingDrivers] = useState(true)
  const [error, setError] = useState('')
  const [mode, setMode] = useState<'form' | 'manual'>('form')

  useEffect(() => {
    setFetchingDrivers(true)
    fetch('/api/drivers/init')
      .then(r => r.json())
      .then(data => {
        setDrivers(data.drivers ?? [])
        setDriverSource(data.source === 'live' ? '✅ Live from IMS' : '📋 Using entry list')
      })
      .catch(() => setError('Could not load drivers. Check your connection.'))
      .finally(() => setFetchingDrivers(false))
  }, [])

  const updatePlayerCount = (n: number) => {
    setPlayerCount(n)
    setPlayerNames(prev => {
      const updated = [...prev]
      while (updated.length < n) updated.push('')
      return updated.slice(0, n)
    })
    setPlayerEmails(prev => {
      const updated = [...prev]
      while (updated.length < n) updated.push('')
      return updated.slice(0, n)
    })
  }

  // Validate before proceeding (either mode)
  const validate = (): boolean => {
    const names = playerNames.map(n => n.trim()).filter(Boolean)
    if (names.length !== playerCount) {
      setError(`Please enter all ${playerCount} player names.`)
      return false
    }
    if (prizes.first <= 0) {
      setError('Enter prize amounts greater than $0.')
      return false
    }
    setError('')
    return true
  }

  // Submit with pre-built assignments (manual mode) or let server assign randomly
  const submitSetup = async (preAssigned?: { playerName: string; drivers: Driver[] }[]) => {
    const names = playerNames.map(n => n.trim()).filter(Boolean)
    setLoading(true)
    try {
      const endpoint = gameSlug
        ? `/api/games/${gameSlug}/setup`
        : '/api/players/setup'
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          playerNames: names,
          playerEmails,
          prizes,
          ...(preAssigned ? { preAssigned } : {})
        })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      onSetupComplete(data.gameId, data.assignments, prizes)
    } catch (err: any) {
      setError(err.message ?? 'Setup failed')
      setMode('form')
    } finally {
      setLoading(false)
    }
  }

  const handleRandomAssign = () => {
    if (!validate()) return
    submitSetup()
  }

  const handleManualAssign = () => {
    if (!validate()) return
    setMode('manual')
  }

  // Called when ManualAssignment screen finishes
  const handleManualComplete = (assignments: { playerName: string; drivers: Driver[] }[]) => {
    submitSetup(assignments)
  }

  const driversPerPlayer = drivers.length
    ? `~${Math.floor(drivers.length / playerCount)}–${Math.ceil(drivers.length / playerCount)} drivers each`
    : ''

  const validNames = playerNames.map(n => n.trim()).filter(Boolean)

  // ── Manual assignment screen ─────────────────────────────────────────────
  if (mode === 'manual') {
    return (
      <ManualAssignment
        players={validNames}
        drivers={drivers}
        onComplete={handleManualComplete}
        onBack={() => setMode('form')}
      />
    )
  }

  // ── Setup form ───────────────────────────────────────────────────────────
  return (
    <div>
      <div className="section-title">🏁 Game Setup</div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="setup-grid">
        {/* LEFT — Players */}
        <div className="card">
          <div style={{ marginBottom: '1rem' }}>
            <div className="form-label">Number of players</div>
            <div className="player-count-row">
              {[1,2,3,4,5,6,7,8,9,10,11].map(n => (
                <button
                  key={n}
                  className={`player-count-btn${playerCount === n ? ' active' : ''}`}
                  onClick={() => updatePlayerCount(n)}
                >{n}</button>
              ))}
            </div>
            {drivers.length > 0 && (
              <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', marginTop: '0.3rem' }}>
                {drivers.length} drivers · {driversPerPlayer}
              </div>
            )}
          </div>

          <div className="form-label">Player names</div>
          <div className="player-inputs">
            {Array.from({ length: playerCount }, (_, i) => (
              <div className="player-input-row" key={i} style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
                <div className="player-number">{i + 1}</div>
                <input
                  className="form-input"
                  placeholder={`Player ${i + 1} name`}
                  value={playerNames[i] ?? ''}
                  onChange={e => {
                    const updated = [...playerNames]
                    updated[i] = e.target.value
                    setPlayerNames(updated)
                  }}
                  style={{ flex: '1 1 140px', minWidth: 0 }}
                />
                <input
                  className="form-input"
                  type="email"
                  placeholder="email (optional)"
                  value={playerEmails[i] ?? ''}
                  onChange={e => {
                    const updated = [...playerEmails]
                    updated[i] = e.target.value
                    setPlayerEmails(updated)
                  }}
                  style={{ flex: '1 1 160px', minWidth: 0, opacity: 0.75 }}
                />
              </div>
            ))}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.4rem' }}>
            Email is optional — players receive their drivers + a live tracking link on race day.
          </div>
        </div>

        {/* RIGHT — Prizes + Driver preview */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
          <div className="card">
            <div className="form-label" style={{ marginBottom: '0.8rem' }}>Prize pool ($)</div>
            <div className="prize-inputs">
              {(['first','second','third'] as const).map((place, i) => (
                <div className="prize-card" key={place}>
                  <div className={`prize-label ${['gold','silver','bronze'][i]}`}>
                    {['🥇 1st','🥈 2nd','🥉 3rd'][i]}
                  </div>
                  <input
                    className="form-input"
                    type="number" min="0" step="5"
                    value={prizes[place]}
                    onChange={e => setPrizes(p => ({ ...p, [place]: Number(e.target.value) }))}
                    style={{ textAlign: 'center' }}
                  />
                </div>
              ))}
            </div>
            <div style={{ marginTop: '0.8rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center' }}>
              Total pool: ${prizes.first + prizes.second + prizes.third}
            </div>
          </div>

          <div className="card">
            <div className="form-label" style={{ marginBottom: '0.5rem' }}>Driver list</div>
            {fetchingDrivers ? (
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                Fetching drivers from IMS…
              </div>
            ) : (
              <>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', marginBottom: '0.6rem' }}>
                  {driverSource} — {drivers.length} drivers ready
                </div>
                <div style={{ maxHeight: '160px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {drivers.slice(0, 10).map(d => (
                    <div className="driver-row" key={d.number}>
                      <DriverAvatar name={d.name} number={d.number} size={32} />
                      <div className="driver-number-badge">#{d.number}</div>
                      <div className="driver-name-text">{d.name}</div>
                      <div className="driver-team-text">{d.team}</div>
                    </div>
                  ))}
                  {drivers.length > 10 && (
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '0.3rem' }}>
                      +{drivers.length - 10} more drivers…
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Assignment mode buttons ── */}
      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.8rem', flexWrap: 'wrap' }}>
        <button
          className="btn btn-secondary"
          onClick={handleManualAssign}
          disabled={loading || fetchingDrivers || drivers.length === 0}
          title="Choose which drivers each player gets"
        >
          ✋ Manually Assign Drivers
        </button>
        <button
          className="btn btn-primary"
          onClick={handleRandomAssign}
          disabled={loading || fetchingDrivers || drivers.length === 0}
        >
          {loading ? 'Setting up…' : '🎲 Randomly Assign & Continue'}
        </button>
      </div>
    </div>
  )
}
